// M3 offshore operations (M3_SPEC §1, §2.1, §5, §10): water-depth envelopes by build year, rig moves & tows, subsea
// BOPs, the offshore drilling-day gate (waiting on weather, supply, ice season, heave) and drilling-tech multipliers
// (top drive, PDC, MWD, motors, heave compensation). Hooks are called from core/actions.js (assignRig) and
// sim/rotary.js; every function returns a neutral value before 1946 so M1/M2 games are untouched.
import { eqDef, fieldDef, stat, rigStats, yf } from '../defs.js';
import { OFFSHORE, DRILL_TECH, STORMS } from '../../data/m3/rules.js';
import { m3On, m3, plotWater, rigBuiltYear, envelope, envelopeProblem, month, fieldNm, serviceRate, problem, envOf, platforms } from './opsutil.js';
import { spend, notify, fmt$ } from '../util.js';
import { hasUpgrade } from '../research.js';
import { chance as chanceFn, nextFloat } from '../../core/rng.js';
import { m3Accident, m3Mitigation } from './opsincidents.js';
import { worldDrillGate } from '../m4/worldHooks.js'; // M4-C
export const SHALLOW_GAS_FT = 2000;          // first 2,000 ft below the mudline, before the BOP is run (§1.3)
export const SHALLOW_GAS_PER_DAY = 0.003;      // est.

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const TUG_RATE_1970 = 2500;          // tow tug $/day (1970 $, est.) for barges/jackups/semis without an AHTS
export const ANCHORS_PER_MOORED_RIG = 8;    // +1 day per 4 anchors on a moored rig move (§2.1)

const gearDef = (state, rig, slot) => { const uid = rig?.gear?.[slot]; return uid && state.items[uid] ? eqDef(state.items[uid].defId) : null; };
export const bopDef = (state, rig) => (rig?.bopUid && state.items[rig.bopUid] ? eqDef(state.items[rig.bopUid].defId) : null);

// Platform that offers a well slot at (fieldId, plot): its own plot or a neighbour within 1 plot (deviated wells).
export function platformAt(state, fieldId, plotIdx, { needRigSlot = false, wellId = null } = {}) {
  const P = state.platforms;
  if (!P) return null;
  const fdef = fieldDef(fieldId);
  const w = fdef?.grid?.w ?? 6;
  const x = plotIdx % w, y = Math.floor(plotIdx / w);
  for (const p of Object.values(P)) {
    if (p.fieldId !== fieldId || p.status === 'BUILDING' || p.status === 'REMOVED') continue;
    const ps = eqDef(p.defId)?.stats ?? {};
    if (ps.subsea) continue;
    const px = p.plot % w, py = Math.floor(p.plot / w);
    if (Math.max(Math.abs(px - x), Math.abs(py - y)) > 1) continue;
    const used = (p.slotsUsed ?? []).filter((id) => id !== wellId && state.wells[id] && state.wells[id].status !== 'PLUGGED');
    if (!wellId || !(p.slotsUsed ?? []).includes(wellId)) { if (used.length >= (ps.slots ?? 0)) continue; }
    if (needRigSlot && (p.rigUids ?? []).filter((u) => state.rigs[u]?.wellId).length >= (ps.rigSlots ?? 0)) continue;
    return p;
  }
  return null;
}

// ---- assignRig hook ------------------------------------------------------------------------------------------------
// Returns null (no M3 rules apply), { ok: false, msg } or { ok: true, extraDays, water, cost, nm }.
export function preAssign(state, rig, fieldId, plotIdx, target) {
  if (!m3On(state)) return null;
  const def = eqDef(rig.defId);
  const fdef = fieldDef(fieldId);
  if (!def || !fdef) return null;
  if (fdef.serviceOnly && yf(state) >= (fdef.serviceOnly.from ?? 0) && yf(state) < (fdef.serviceOnly.until ?? 9999)) return { ok: false, msg: `${fdef.name} belongs to ${fdef.serviceOnly.operator}: you can only charter rigs to them.` };
  const water = plotWater(fieldId, plotIdx);
  const built = rigBuiltYear(rig);
  const e = envelope(def, built);
  const bad = envelopeProblem(def, built, water, target ?? 0);
  if (bad) return { ok: false, msg: bad };
  if (e.offshore && e.bopType === 'subsea') {
    const b = bopDef(state, rig);
    if (!b || stat(b, 'bopType', 'surface') !== 'subsea') return { ok: false, msg: `${rig.name} is a floater: it needs a subsea BOP stack (a surface stack cannot be used on a floating rig).` };
  }
  if (e.needsPlatform && !platformAt(state, fieldId, plotIdx, { needRigSlot: true, wellId: state.fields[fieldId]?.plots[plotIdx]?.wellId })) {
    return { ok: false, msg: `${rig.name} must stand on a production platform with a free well slot and rig slot at this plot — build one first.` };
  }
  const ice = envOf(fieldId)?.ice;
  const wellHere = state.fields[fieldId]?.plots[plotIdx]?.wellId;
  if (ice?.drillSeason && !e.coldRated && !ice.drillSeason.includes(month(state)) && !wellHere) {
    return { ok: false, msg: `${fdef.name}: rigs that are not winterized move and spud only in the ice-road season (${ice.drillSeason.map((m) => MONTHS[m]).join(', ')}).` };
  }
  // move between fields (tow / transit / truck), and anchor handling for moored rigs
  const from = rig.location?.fieldId ?? rig.at?.fieldId ?? null;
  const nm = from && from !== fieldId ? fieldNm(from, fieldId) : 0;
  let extra = 0, cost = 0;
  if (e.offshore) {
    const kn = stat(def, 'transitKnots', 4);
    const transit = nm > 0 ? (kn > 0 ? nm / (kn * 24) : 0) : 0;
    const storm = state.fields[fieldId]?.storm;
    if (nm > 0 && e.rigClass === 'jackup' && storm && storm.kind === 'hurricane' && state.day <= storm.untilDay) return { ok: false, msg: `${storm.name ?? 'A hurricane'} is forecast on the route — a jackup may not tow now.` };
    const moveDays = stat(def, 'moveDays', 3);
    let anchorDays = 0;
    if (e.stationKeeping === 'moored') {
      const ah = anchorHandlerFor(state, rig);
      if (!ah) return { ok: false, msg: `${rig.name} is moored: contract an anchor handler (AHTS) for the move first.` };
      anchorDays = ANCHORS_PER_MOORED_RIG / 4;
      cost += serviceRate(state, eqDef(ah.defId)) * (moveDays + anchorDays + transit) * OFFSHORE.rigMoveCostDays;
    } else if (!stat(def, 'selfPropelled', false) && e.stationKeeping !== 'platform') {
      cost += eraTug(state) * (moveDays + transit) * OFFSHORE.rigMoveCostDays;
    }
    extra = transit + anchorDays + (from && from !== fieldId ? moveDays : 0);
  } else if (nm > 0) {
    extra = 0.5 * (nm * 1.15) / 100; // statute miles by truck
  }
  if (cost > 0 && state.company.cash < cost) return { ok: false, msg: `The rig move costs ${fmt$(cost)} in tugs/anchor handling.` };
  return { ok: true, extraDays: extra, water, cost, nm, from, offshore: e.offshore };
}

const eraTug = (state) => TUG_RATE_1970 * Math.max(0.2, (yf(state) - 1900) / 70); // crude CPI-ish ramp (est.)

export function anchorHandlerFor(state, rig) {
  return Object.values(state.logistics ?? {}).find((l) => l.assignedTo?.kind === 'rig' && l.assignedTo.id === rig.uid && stat(eqDef(l.defId), 'service', '') === 'anchor') ?? null;
}

// After the well exists and the rig-up timer started.
export function afterAssign(state, rig, well, info) {
  if (!info) return;
  if (info.cost > 0) spend(state, info.cost, 'logistics', false);
  stampWell(state, well, info.water);
  const def = eqDef(rig.defId);
  const e = envelope(def, rigBuiltYear(rig));
  rig.offshore = e.offshore;
  rig.location = { fieldId: well.fieldId, plot: well.plot };
  if (info.nm > 0) {
    const days = Math.round(info.extraDays);
    rig.location.transit = { from: info.from, to: well.fieldId, nm: Math.round(info.nm), days, startDay: state.day, untilDay: state.day + days };
    notify(state, 'rigMove', { rigUid: rig.uid, fieldId: well.fieldId, days, phase: 'tow', text: `${rig.name} under tow to ${fieldDef(well.fieldId)?.name} (${Math.round(info.nm)} nm, ~${days} days).` });
    if (e.offshore) m3(state).supply[rig.uid] = e.deckStorageDays; // loads out fresh at the shore base
  }
  if (e.offshore) m3(state).supply[rig.uid] ??= e.deckStorageDays;
  const maxBml = e.maxDepth - (well.waterDepthFt ?? 0);
  if (well.target > maxBml) well.target = Math.max(well.depth + 1, maxBml);
  const p = platformAt(state, well.fieldId, well.plot, { wellId: well.id });
  if (p) {
    well.platformUid = p.uid;
    if (!p.slotsUsed.includes(well.id)) p.slotsUsed.push(well.id);
    if (e.needsPlatform && !p.rigUids.includes(rig.uid)) p.rigUids.push(rig.uid);
  }
}

export function stampWell(state, well, water = null) {
  const wtr = water ?? plotWater(well.fieldId, well.plot);
  well.waterDepthFt = wtr;
  well.offshore = wtr > 0;
  well.md = Math.round(wtr + (well.depth ?? 0));
}

// ---- the drilling-day gate (sim/rotary.js) ----------------------------------------------------------------------------
// null before 1946 / nothing to do; { block: true } stops today's drilling; { time } = share of the day left to drill.
export function m3DrillGate(state, well, ctx) {
  if (!m3On(state)) return null;
  const g4 = worldDrillGate(state, well, ctx); // M4-C: moratorium, sanctions, sea ice, sour gas, BOP pulls, dual activity, salt creep (null before 2000)
  if (g4?.block) return g4;
  const def = ctx.rigDef;
  const s = def?.stats ?? {};
  const env = envOf(well.fieldId);
  if (env?.ice?.drillSeason && !s.coldRated && !env.ice.drillSeason.includes(month(state))) {
    problem(state, well, 'ICE_SEASON', `the ice roads are gone — a non-winterized rig must wait for ${MONTHS[env.ice.drillSeason[0]]}.`);
    return { block: true };
  }
  if (well.offshore === undefined) stampWell(state, well);
  if (!well.offshore) return null;
  well.md = Math.round((well.waterDepthFt ?? 0) + well.depth);
  const sea = state.fields[well.fieldId]?.sea;
  const hs = sea && sea.day >= state.day - 1 ? sea.hsFt : 0;
  well.seaState = hs;
  if (well.evacuated) { problem(state, well, 'WOW', 'evacuated for the storm — the rig is unmanned.'); well.wowDays = (well.wowDays ?? 0) + 1; return { block: true }; }
  if ((well.mooringUntil ?? 0) > state.day) { problem(state, well, 'WOW', 'drifted off location — re-running anchors and riser.'); return { block: true }; }
  const opLimit = s.opLimitHsFt ?? 15;
  if (hs > opLimit) {
    problem(state, well, 'WOW', `waiting on weather: ${Math.round(hs)}-ft seas (limit ${opLimit} ft).`);
    well.wowDays = (well.wowDays ?? 0) + 1;
    return { block: true };
  }
  const key = ctx.rig?.uid ?? well.id;
  const M = m3(state);
  M.supply[key] ??= s.deckStorageDays ?? 20;
  if (M.supply[key] <= 0) { problem(state, well, 'NO_SUPPLY', 'out of mud, cement and fuel on deck — contract a supply boat.', { pause: true }); return { block: true }; }
  M.supply[key] = Math.max(0, M.supply[key] - 1);
  if (env?.shallowGas && well.depth < SHALLOW_GAS_FT && ctx.rig && chanceFn(state, SHALLOW_GAS_PER_DAY * (1 - m3Mitigation(state, ctx.rig, 'shallow_gas')))) {
    const dmg = 20 + 40 * nextFloat(state);
    ctx.rig.condition = Math.max(0, ctx.rig.condition - dmg);
    m3Accident(state, 'shallow_gas', { rig: ctx.rig, well, injured: 1, text: `Gas from a shallow sand ${Math.round(well.depth)} ft below the seabed, before the BOP was on — the diverter ${m3Mitigation(state, ctx.rig, 'shallow_gas') > 0 ? 'held' : 'barely held'} (rig −${Math.round(dmg)}%).` });
    return { block: true };
  }
  let time = 1;
  if (s.floating) {
    const comp = gearDef(state, ctx.rig, 'motion');
    const mult = comp ? stat(comp, 'heaveDowntimeMult', 0.4) : 1;
    time -= Math.min(0.9, OFFSHORE.heaveDowntimePerFtHs * Math.max(0, hs - 5) * mult);
  }
  if (env?.currentKn > 2 && s.stationKeeping === 'dp' && ((state.day * 7) % 10) === 0) time *= 0; // loop-current eddy days (10%)
  if (ctx.rig && !ctx.contractor && s.offshoreRoles?.crane_op && !ctx.cf.crew.some((c) => c.role === 'crane_op')) time *= 0.5;
  return { time: Math.max(0, time) * (g4?.time ?? 1) }; // M4-C: dual-activity / salt-creep time
}

// ---- drilling technology (§10) ------------------------------------------------------------------------------------
// ROP multiplier on top of the M2 rotary model. h = stratum hardness, rpm = rpm setting, rpmExpBase = M2 exponent.
export function m3RopMult(state, well, ctx, h, rpm, rpmExpBase, overHardBase, mdef) {
  const bdef = ctx.bitDef;
  let f = 1;
  if (bdef?.stats?.pdc) {
    const T = DRILL_TECH.pdc;
    f *= Math.pow(Math.max(0.1, rpm), T.ropRpmExp - rpmExpBase);
    const mh = bdef.stats.maxHard ?? 6;
    if (h > mh) f *= Math.max(0.05, 1 - T.hardPenaltyPerPoint * (h - mh)) / Math.max(0.05, overHardBase);
    if (mdef && T.bestWithMud.includes(mdef.id)) f *= T.oilMudSpeedMult;
  }
  const rig = ctx.rig;
  if (ctx.rigDef?.stats?.topDrive) f *= 1.12;
  if (rig?.gear) {
    for (const slot of ['drive', 'motor', 'solids']) { const g = gearDef(state, rig, slot); if (g) f *= stat(g, 'ropMult', 1); }
    if (rig.gear.mwd && gearDef(state, rig, 'mwd')) f *= 1 + DRILL_TECH.mwd.surveyDaysSavedPer1000Ft; // no survey stops
  }
  return f;
}

export function m3TripMult(state, rig, rigDef = null) {
  let f = 1;
  const def = rigDef ?? eqDef(rig?.defId);
  if (def?.stats?.topDrive) f *= 0.8;
  if (rig?.gear) for (const slot of ['drive', 'mwd']) { const g = gearDef(state, rig, slot); if (g) f *= stat(g, 'tripMult', 1); }
  return f;
}

// PDC cutter loss in rock above the bit's rating (impactFailPerDay × (h − maxHard + 1)), diamond impact damage.
export function m3BitDamage(state, well, ctx, h, chanceFn) {
  const bdef = ctx.bitDef;
  const p0 = bdef?.stats?.impactFailPerDay;
  if (!p0 || !ctx.bit || ctx.contractor) return;
  const mh = bdef.stats.maxHard ?? 6;
  const p = h > mh ? Math.min(0.9, p0 * 10 * (h - mh + 1)) : p0;
  if (chanceFn(p)) {
    ctx.bit.condition = Math.max(0, ctx.bit.condition - 25);
    if (h > mh) problem(state, well, 'CUTTERS', `the ${bdef.name} is losing cutters in ${h > mh + 2 ? 'very ' : ''}hard rock — trip for a roller cone.`);
  }
}

// Horizontal well (upgrade + steerable motor + MWD on the rig): qi × (1 + lateral/1000), capped (§10).
export function m3QiMult(state, well) {
  if (!well.horizontal?.lateralFt) return 1;
  const T = DRILL_TECH.horizontal;
  if (!well.horizontal.charged) { const extra = (well.cost ?? 0) * ((well.horizontal.costMult ?? 1.6) - 1); spend(state, extra, 'consumables', false); well.cost += extra; well.horizontal.charged = Math.round(extra); }
  return Math.min(T.maxQiMult, 1 + T.qiMultPer1000Ft * well.horizontal.lateralFt / 1000);
}

export function canDrillHorizontal(state, rig) {
  if (!hasUpgrade(state, 'horizontal_drilling')) return 'Research horizontal drilling first.';
  const motor = gearDef(state, rig, 'motor'), mwd = gearDef(state, rig, 'mwd');
  if (!motor || !stat(motor, 'steerable', false)) return 'Needs a steerable motor on the rig.';
  if (!mwd) return 'Needs an MWD tool on the rig.';
  return null;
}

// Offshore crews: back-to-back hitches and the offshore wage premium, charged on top of the land wages (§5).
export function offshoreWageExtra(state, rig, landWages) {
  if (!rig.offshore || !rig.wellId) return 0;
  const w = state.wells[rig.wellId];
  if (!w?.offshore) return 0;
  return landWages * (1.5 * OFFSHORE.backToBackCrews - 1);
}

export const STORM_DAMAGE = STORMS.damage;
export { platforms };
export const isOffshoreRig = (rig) => !!eqDef(rig?.defId)?.stats?.offshore;
export const rigTypeOf = (rig) => rigStats(eqDef(rig?.defId)).rigType;
